import { useState, useEffect } from "react";
import styles from "./EditReasonModal.module.scss";

const EditReasonModal = ({
  entry,
  contacts,
  onSave,
  onCancel,
  loading,
  error
}) => {
  const [reason, setReason] = useState("");
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (entry) {
      setReason(entry.reason || "");
    }
  }, [entry]);

  const validateForm = () => {
    const errors = {};

    if (reason.length > 500) {
      errors.reason = "Reason must be 500 characters or less";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // For contact-type blacklist entries, we need to include the contact ID
      const updateData = { reason: reason.trim() || null };
      
      if (entry.blacklist_type === 'contact') {
        // Find contact ID by matching email from contacts list
        const contactEmail = entry.contact_email;
        
        if (contacts?.data && contactEmail) {
          const matchingContact = contacts.data.find(contact => 
            contact.email === contactEmail || contact.contact_email === contactEmail
          );
          
          if (matchingContact && matchingContact.id) {
            updateData.contact = matchingContact.id;
          }
        }
      }
      
      await onSave(updateData);
      handleCancel();
    } catch (error) {
      // Error handling is managed by parent component
    }
  };

  const handleCancel = () => {
    setReason("");
    setValidationErrors({});
    onCancel();
  };

  const handleReasonChange = (e) => {
    const value = e.target.value;
    setReason(value);
    setValidationErrors(prev => ({ ...prev, reason: null }));
  };

  if (!entry) return null;

  const getDisplayName = () => {
    if (entry.blacklist_type === 'contact') {
      return `${entry.contact_name || 'Unknown'} (${entry.contact_email || 'no email'})`;
    } else if (entry.blacklist_type === 'domain') {
      return entry.domain || 'Unknown domain';
    }
    return entry.blacklist_display_name || 'Unknown';
  };

  const getTypeDisplay = () => {
    if (entry.blacklist_type === 'contact') {
      return '👤 Contact';
    } else if (entry.blacklist_type === 'domain') {
      return '🌐 Domain';
    }
    return '❓ Unknown';
  };

  return (
    <div className={styles.form}>
      <div className={styles.header}>
        <h2>Edit Blacklist Reason</h2>
      </div>

      <div className={styles.content}>
        {/* Entry Info */}
        <div className={styles.entryInfo}>
          <div className={styles.entryType}>
            {getTypeDisplay()}
          </div>
          <div className={styles.entryName}>
            {getDisplayName()}
          </div>
          {entry.contact_customer_name && (
            <div className={styles.customerName}>
              Customer: {entry.contact_customer_name}
            </div>
          )}
        </div>

        {/* Reason Input */}
        <div className={styles.section}>
          <label className={styles.sectionLabel} htmlFor="reasonInput">
            Reason for Blacklisting
          </label>
          <textarea
            id="reasonInput"
            className={`${styles.textArea} ${validationErrors.reason ? styles.error : ''}`}
            placeholder="Explain why this contact/domain should be blacklisted..."
            value={reason}
            onChange={handleReasonChange}
            rows={4}
            maxLength={500}
          />
          <div className={styles.charCount}>
            {reason.length}/500 characters
          </div>
          {validationErrors.reason && (
            <div className={styles.fieldError}>{validationErrors.reason}</div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className={styles.errorMessage}>
            {error}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button 
          className={styles.cancelButton} 
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button 
          className={styles.saveButton} 
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default EditReasonModal;